import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import { useForm } from 'react-hook-form';

import { VpHookFormWrapper } from '../../wrappers';
import { useFetchAllCampaigns } from '../../../hooks';
import { useImportCampaignSubmission } from '../../../hooks/react-query/useSubmissionsData';
import { getDateData } from '../../../utils/date-utils';
import { useVpSnackbar } from '../../hooks';

import VpDialogContent from '../../components/VpDialogContent/VpDialogContent';
import ImportMediaTagInput from '../../components/ImportMediaTagInput';
import VpButton from '../../components/VpButton';

import { VpHookFormInput, VpHookFormSelect } from '../../inputs';
import { VpDialogTitle, VpInfoBanner, VpHookFormFileDragAndDrop } from '../../components';

export const ImportMediaModal = ({ open = false, onClose = () => {} }) => {
  const { register } = useForm();
  const { data } = useFetchAllCampaigns();
  const { addSnackbarSuccess } = useVpSnackbar();
  const { mutateAsync: importCampaignSubmission } = useImportCampaignSubmission();

  const [canSubmitForm, setCanSubmitForm] = React.useState(true);
  const [selectedTags, setSelectedTags] = React.useState([]);
  const [files, setFiles] = React.useState([]);

  const campaigns = useMemo(
    () => data?.map((m) => ({ id: m?.value, label: m?.label })) || [],
    [data]
  );

  const onSubmit = async (formData) => {
    // Prepare payload for campaign submission endpoint
    const payload = {
      tags: selectedTags.map((t) => t.id),
      campaignId: formData.campaignId,
      responses: []
    };

    files?.forEach((item) => {
      const contentPayload = item.attachmentId
        ? {
            attachment: {
              id: item.attachmentId,
              contentType: item.contentType,
              extension: item.extension,
              filename: item.filename,
              originalFilename: item.originalFileName,
              size: item.size,
              path: item.path
            }
          }
        : {
            mediaSubmissionId: item.mediaSubmissionId
          };

      payload.responses.push(contentPayload);
    });

    formData.name && payload.responses.push({ value: formData.name });
    formData.email && payload.responses.push({ value: formData.email });

    await importCampaignSubmission(payload);
    addSnackbarSuccess({
      message:
        'All done! Check your Media tab in a couple of minutes and you should see your newly imported content.'
    });
    onClose();
  };

  const date = getDateData({ includeYear: true });

  const tite = (
    <Stack direction="row" gap="10px" alignItems="center">
      <Typography variant="inputLabel">Import Content</Typography>
      <Typography variant="caption">{date}</Typography>
    </Stack>
  );

  const FORM_LABEL =
    'Use this form to quickly import content into the platform. Select one of your Campaigns to hold the new data and optionally include additional info such as Name and Email.';

  return (
    <Dialog disableEscapeKeyDown maxWidth="md" fullWidth onClose={onClose} open={open}>
      <VpDialogTitle leftContent={tite} onClose={onClose} />
      <VpDialogContent>
        <VpHookFormWrapper onSubmit={onSubmit}>
          <Stack gap="20px" alignItems="center" justifyContent="center" p="20px">
            <VpInfoBanner label={FORM_LABEL} />
            <VpHookFormSelect
              name="campaignId"
              label="Campaign"
              placeholder="Parent Campaign"
              options={campaigns || []}
              required
              smallLabel
              disableHelperText
              {...register('campaignId')}
            />
            <VpHookFormInput
              name="name"
              label="Name"
              placeholder="Enter submitter's name if available"
              smallLabel
              disableHelperText
              {...register('name')}
            />
            <VpHookFormInput
              name="email"
              label="Email"
              placeholder="Enter submitter's email if available"
              isEmail
              smallLabel
              disableHelperText
              {...register('email')}
            />
            <VpHookFormFileDragAndDrop
              name="content"
              setCanSubmitForm={setCanSubmitForm}
              setFiles={setFiles}
              files={files}
            />
            <ImportMediaTagInput
              name="tags"
              tagsStateHandler={setSelectedTags}
              value={selectedTags}
            />
            <VpButton variant="contained" type="submit" disabled={!canSubmitForm}>
              Submit
            </VpButton>
          </Stack>
        </VpHookFormWrapper>
      </VpDialogContent>
    </Dialog>
  );
};

ImportMediaModal.propTypes = {
  onClose: PropTypes.func,
  open: PropTypes.bool
};

export default ImportMediaModal;



// WEBPACK FOOTER //
// ./src/vp-ui/dialogs/ImportMediaModal/ImportMediaModal.jsx